/**
 * Turns a serialized Bear node into the key/value rows the inspector overlay
 * displays: the props the model code wrote, plus the specification the part
 * definition publishes through `columns()` (already unit-formatted).
 */

import type { CrossSectionData, PartColumn } from "bear/engine/part";
import type { SourceLocation } from "bear/engine/types";
import type { EditorSelection } from "forest/monaco-editor/MonacoEditor";
import {
  PROJECT_EXTENSIONS,
  resolveProjectPathWith,
} from "playground/pipeline/resolve";

import type { SerializedNode } from "@/bear-sandbox/query";

import { formatDimensions } from "./parts-utils";

export interface InspectorRow {
  key: string;
  /** Polygon values are drawn as a shape rather than written out. */
  value: string | CrossSectionData;
}

/** Serialization artifacts for the parts list and BOM, not model properties. */
const HIDDEN_PROPS = new Set(["partColumns", "bomExcludeKeys"]);

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function isPartColumn(value: unknown): value is PartColumn {
  return typeof value === "object" && value !== null && "key" in value;
}

/** The short name of a node: its own FQN segment, dots and all. */
export function nodeLabel(node: SerializedNode): string {
  return node.segment;
}

/** Every prop the model code wrote, verbatim. */
export function propRows(node: SerializedNode): InspectorRow[] {
  return Object.entries(node.props)
    .filter(([key]) => !HIDDEN_PROPS.has(key))
    .map(([key, value]) => ({ key, value: formatValue(value) }));
}

/**
 * The specification columns the part publishes, which carry human labels and
 * are already formatted in the part's units.
 */
export function specificationRows(node: SerializedNode): InspectorRow[] {
  const columns = node.props.partColumns;
  if (!Array.isArray(columns)) return [];
  return columns.filter(isPartColumn).map((column) => ({
    key: column.label ?? column.key,
    value: Array.isArray(column.value)
      ? column.value
      : formatValue(column.value),
  }));
}

/**
 * How each relation reads from the node being inspected. Both directions are
 * written out in full rather than derived from the kind, because a connector
 * reads one way from the member it hangs on and the other from the member it
 * seats. An unregistered kind (a model may register its own) shows its name.
 */
const CONNECTION_LABELS: Record<string, { out: string; in: string }> = {
  bears: { out: "Bears on", in: "Carries" },
  fastens: { out: "Fastened to", in: "Fastened by" },
  feeds: { out: "Feeds", in: "Fed by" },
  cuts: { out: "Cuts", in: "Cut by" },
};

/** One edge, as the inspector shows it: a relation and a node to walk to. */
export interface ConnectionRow {
  /** The relation, read from the inspected node. */
  key: string;
  /** The other endpoint's FQN: what selecting the row selects. */
  fqn: string;
  /** The other endpoint's short name. */
  label: string;
  /** The edge's own data — a connector's fastener schedule, a tributary share. */
  detail: InspectorRow[];
}

/**
 * The edge's data, a row per entry. An entry that is itself a record gets a
 * row per field instead of a line of JSON, which is what makes a connector's
 * fastener row (`{ product, count }`) readable.
 */
function edgeRows(props: Record<string, unknown>): InspectorRow[] {
  return Object.entries(props).flatMap(([key, value]) =>
    isRecord(value)
      ? Object.entries(value).map(([field, inner]) => ({
          key: `${key}.${field}`,
          value: formatValue(inner),
        }))
      : [{ key, value: formatValue(value) }],
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** What the node is attached to, and through which relation. */
export function connectionRows(node: SerializedNode): ConnectionRow[] {
  return node.edges.map((edge) => ({
    key: CONNECTION_LABELS[edge.kind]?.[edge.direction] ?? edge.kind,
    fqn: edge.fqn,
    label: edge.segment,
    detail: edgeRows(edge.props),
  }));
}

/** The measured extents, if the node was measured. */
export function dimensionRows(node: SerializedNode): InspectorRow[] {
  if (!node.extents) return [];
  return [{ key: "Dimensions", value: formatDimensions(node.extents) }];
}

// ---------------------------------------------------------------------------
// Jump to source
// ---------------------------------------------------------------------------

export interface SourceJumpTarget {
  path: string;
  selection: EditorSelection;
}

/** A call site resolved to a workspace path an editor or reader can open. */
export interface SourceFrame {
  path: string;
  line: number;
  column: number;
}

/**
 * Normalize a compiler fileName to a workspace path the editor can open.
 * Builder-loaded files arrive as `project:<path>` and are project files by
 * construction. Library files compiled by the app's own build arrive as
 * absolute repo paths (`/…/packages/bear/…`), which map onto their mounted
 * location under `node_modules/`; only absolute paths get that mapping, so a
 * project directory that happens to be named `packages` is left alone.
 */
function workspacePath(fileName: string): string {
  if (fileName.startsWith("/")) {
    const repoPackage = fileName.match(/\/packages\/([^/]+\/.+)$/);
    if (repoPackage) return `node_modules/${repoPackage[1]}`;
    return fileName.replace(/^\/+/, "");
  }
  return fileName.replace(/^project:/, "");
}

function hasSourceExtension(path: string): boolean {
  return PROJECT_EXTENSIONS.some((ext) => path.endsWith(ext));
}

/**
 * A node's call sites as openable workspace paths, innermost first. Import
 * specifiers may omit the extension or name a directory, so frames are resolved
 * against the project with the same candidate order imports use; mounted
 * library files are invisible to `fileExists` and pass through on their
 * extension alone. A frame that resolves to nothing openable drops out.
 */
export function sourceChainFrames(
  chain: readonly SourceLocation[],
  fileExists: (path: string) => boolean,
): SourceFrame[] {
  return chain.flatMap((frame) => {
    const candidate = workspacePath(frame.fileName);
    const path =
      resolveProjectPathWith(fileExists, candidate) ??
      (hasSourceExtension(candidate) ? candidate : null);
    if (!path) return [];
    return [{ path, line: frame.lineNumber, column: frame.columnNumber }];
  });
}

/**
 * Where "jump to source" should land for a node: the innermost call site in
 * project code, falling back to the innermost frame outright (a node whose
 * whole chain is library code).
 */
export function sourceJumpTarget(
  chain: readonly SourceLocation[],
  fileExists: (path: string) => boolean,
): SourceJumpTarget | null {
  const frames = sourceChainFrames(chain, fileExists);
  const frame =
    frames.find((f) => !f.path.startsWith("node_modules/")) ?? frames[0];
  if (!frame) return null;

  return {
    path: frame.path,
    selection: { startLineNumber: frame.line, startColumn: frame.column },
  };
}
